import { useState } from 'react';
import { Err } from '../ui.js';
import { login, signup, forgotPassword, ApiError } from '../api.js';
import type { Identity } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// THE FRONT DOOR.
//
// This replaces the paste-your-CRM_API_SECRET screen, which asked a person to
// find a value in a .env file. That is a reasonable ask of the one developer
// who wrote it and an absurd one of anybody else — and it could not tell two
// people apart, so nothing could be attributed and nobody could be removed.
//
// The old screen is not gone. A workspace key still signs in, because scripts,
// the scheduler and the drills use one and a machine should not have a
// password. It is behind a link rather than in the way.
//
// SIGN IN AND SIGN UP ARE ONE SCREEN with a toggle, not two routes. Somebody
// arriving at a CRM they were handed does not know which of the two they are,
// and making them guess right before they can see anything is a bad first
// question.
// ─────────────────────────────────────────────────────────────────────────────

type Mode = 'in' | 'up' | 'key' | 'forgot';

export function SignIn({ onIn, onKey }: {
  onIn: (id: Identity & { token: string }) => void;
  /** The old path: a raw workspace key. Kept for machines and for Kyle. */
  onKey: (secret: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function go() {
    setBusy(true); setError(null);
    try {
      const r = mode === 'in'
        ? await login(email.trim(), password)
        : await signup({ email: email.trim(), password, name: name.trim(), company: company.trim() });
      onIn(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  const ready = mode === 'in'
    ? email.trim().length > 3 && password.length > 0
    : email.trim().length > 3 && password.length >= 10 && company.trim().length > 0;

  // ── forgot ────────────────────────────────────────────────────────────────
  // The confirmation is deliberately the SAME whatever happened, because the
  // server answers the same way whatever happened. Branching here — "no account
  // with that address" — would rebuild the enumeration oracle the endpoint
  // refuses to be, on the client, where it is just as readable.
  if (mode === 'forgot') {
    return (
      <div className="signin">
        <form className="signincard" onSubmit={(e) => {
          e.preventDefault();
          if (email.trim().length > 3 && !busy) {
            setBusy(true); setError(null);
            forgotPassword(email.trim())
              .then(() => setSent(true))
              .catch((x: unknown) => setError(x instanceof ApiError ? x.message : String(x)))
              .finally(() => setBusy(false));
          }
        }}>
          <span className="welcomelogo"><img src="/logo.png" alt="" /></span>
          <h1>Reset your password</h1>
          <Err>{error}</Err>

          {sent ? (
            <>
              <p className="fieldhint">
                If that address has an account, a reset link is on its way. It
                works once and expires in an hour.
              </p>
              <p className="fieldhint">
                Nothing arrived? Check spam, then try again — and remember the
                link only ever goes to the address on the account.
              </p>
              <div className="actions">
                <button className="primary" type="button"
                  onClick={() => { setSent(false); setMode('in'); }}>
                  Back to sign in
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="fieldhint">
                Type the address you sign in with. We will send a link that lets
                you choose a new password.
              </p>
              <label className="field">
                <span className="field-label">Email</span>
                <input type="email" value={email} autoFocus autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)} />
              </label>
              <div className="actions">
                <button className="primary" type="submit"
                  disabled={email.trim().length < 4 || busy}>
                  {busy ? 'Sending…' : 'Send the link'}
                </button>
                <button className="ghost" type="button" onClick={() => setMode('in')}>
                  Back
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    );
  }

  if (mode === 'key') {
    return (
      <div className="signin">
        <form className="signincard" onSubmit={(e) => { e.preventDefault(); onKey(key.trim()); }}>
          <span className="welcomelogo"><img src="/logo.png" alt="" /></span>
          <h1>Workspace key</h1>
          <p className="lede">
            The <code>CRM_API_SECRET</code> from your <code>.env</code>, or a key from{' '}
            <code>npm run org:new</code>. Held in this browser tab only.
          </p>
          <label className="field">
            <span className="field-label">Key</span>
            <input type="password" value={key} autoFocus autoComplete="off"
              placeholder="crm_… or your deployment secret"
              onChange={(e) => setKey(e.target.value)} />
          </label>
          <div className="actions">
            <button className="primary" type="submit" disabled={!key.trim()}>Unlock</button>
            <button className="ghost" type="button" onClick={() => setMode('in')}>
              Sign in with email instead
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="signin">
      <form className="signincard" onSubmit={(e) => { e.preventDefault(); if (ready) void go(); }}>
        <span className="welcomelogo"><img src="/logo.png" alt="" /></span>
        <h1>{mode === 'in' ? 'Sign in' : 'Create your workspace'}</h1>
        <p className="lede">
          {mode === 'in'
            ? 'Your email and password.'
            : 'One account, one workspace, yours. The next screen asks what you '
              + 'sell and sets the rest up from your answer.'}
        </p>

        <Err>{error}</Err>

        {mode === 'up' && (
          <div className="pair">
            <label className="field">
              <span className="field-label">Your name</span>
              <input value={name} autoComplete="name"
                onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span className="field-label">Company</span>
              <input value={company} autoComplete="organization" placeholder="Jaxen Consulting"
                onChange={(e) => setCompany(e.target.value)} />
            </label>
          </div>
        )}

        <label className="field">
          <span className="field-label">Email</span>
          <input type="email" value={email} autoFocus={mode === 'in'}
            autoComplete="username" onChange={(e) => setEmail(e.target.value)} />
        </label>

        <label className="field">
          <span className="field-label">Password</span>
          <input type="password" value={password}
            autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
            onChange={(e) => setPassword(e.target.value)} />
          {mode === 'up' && (
            <span className="field-hint">
              At least 10 characters. Length beats punctuation.
            </span>
          )}
        </label>

        <div className="actions">
          <button className="primary" type="submit" disabled={busy || !ready}>
            {busy ? 'One moment…' : mode === 'in' ? 'Sign in' : 'Create workspace'}
          </button>
        </div>

        <p className="fieldhint">
          {mode === 'in' ? (
            <>
              No account yet?{' '}
              <button className="linkish" type="button" onClick={() => { setMode('up'); setError(null); }}>
                Create one
              </button>
              {' · '}
            </>
          ) : (
            <>
              Already have one?{' '}
              <button className="linkish" type="button" onClick={() => { setMode('in'); setError(null); }}>
                Sign in
              </button>
              {' · '}
            </>
          )}
          <button className="linkish" type="button" onClick={() => { setMode('key'); setError(null); }}>
            Use a workspace key
          </button>
        </p>

        {/* This used to say there was no reset and point at a shell command.
            xsellr8now.com verified on 2026-08-19, so the reason expired and
            the dead end with it. */}
        {mode === 'in' && (
          <p className="fieldhint">
            <button type="button" className="linkish"
              onClick={() => { setError(null); setMode('forgot'); }}>
              Forgotten your password?
            </button>
          </p>
        )}
      </form>
    </div>
  );
}
